import {
  Flex,
  Button,
  Grid,
  Input,
  Box,
  Radio,
  Text,
  Select,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';
import { useCreateWorkflow, useUpdateWorkflow } from '../../../../services/workflow.services';
import {
  useCreateAIFMPeriod,
  useCreatePeriod,
  useCreatePeriodAutomatic,
  useUpdatePeriod
} from '../../../../services/period.service';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { AddAIFs } from '../../Funds/components/AddAIFs';
import { useCreateFundsBulk } from '../../../../services/funds.service';
import { FormInput } from '../../../../components/FormElements/FormInput';
import { REPORT_NAMINGS, REPORT_TYPES, reportTypesList } from '../../../../consts/reportTypes';
import authStore from '../../../../store/auth';
import { PERMISSION_KEYS } from '../../../../consts/permissions';
import { useFeatureFlag, FEATURE_FLAG_NAMES } from '../../../../hooks/useFeatureFlag';
import { getErrorMessage, isOxFlowLocation, getYearsForLastFiveYears } from '../../../../utils';
import {
  CURRENT_REPORTS_SECTION_KEYS,
  CURRENT_REPORTS_SECTION_OPTIONS,
  FILLING_TYPE,
  FILLING_TYPE_ARRAYS,
  FORM_PF_FILLING_TYPE_KEYS,
  FORM_PF_FILLING_TYPE_OPTIONS,
  PERIODIC_FILLING_VERSION_KEYS,
  PERIODIC_FILLING_VERSION_OPTIONS
} from './data';
import FormPFDeadlineCalculator from './components/FormPFDeadlineCalculator';
import dayjs from '../../../../utils/dayjs';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { reportError } from '../../../../services/logger';

interface CreateProjectDefaultValues {
  id?: string;
  projectName?: string;
  period?: string;
  year?: string | number;
  endDate?: string;
  filing_type?: string;
  filing_time?: string;
  version?: string;
}

interface CreateProjectProps {
  refetch?: () => void;
  onClose?: () => void;
  onOpenAddAifs?: () => void;
  isOpenAddAifs?: boolean;
  selectedReportType: string;
  setSelectedReportType?: (type: string) => void;
  isUpdate?: boolean;
  defaultValues?: CreateProjectDefaultValues;
  workflowId?: string;
  isAutomation?: boolean;
  submitLabel?: string;
}

interface QuaterLabelsByKey {
  [key: string]: string;
}

const quaters = ['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'Y1', 'X1', 'X2'];
const quatersFormPF = ['Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4', 'Annual'];
const quaterKeysByLabel: Record<string, string> = {
  'Quarter 1': 'Q1',
  'Quarter 2': 'Q2',
  'Quarter 3': 'Q3',
  'Quarter 4': 'Q4',
  Annual: 'Y1'
};
const quaterLabelsByKey: QuaterLabelsByKey = {
  Q1: 'Quarter 1',
  Q2: 'Quarter 2',
  Q3: 'Quarter 3',
  Q4: 'Quarter 4',
  Y1: 'Annual',
  X1: 'Quarter 1',
  X2: 'Quarter 1',
  H1: 'Quarter 1',
  H2: 'Quarter 1'
};
const years = getYearsForLastFiveYears();

const quatersByReportType: Record<string, readonly string[] | string[]> = {
  [REPORT_TYPES.FORM_PF]: quatersFormPF,
  [REPORT_TYPES.ANNEX_IV]: quaters,
  [REPORT_TYPES.CUSTOM]: quaters,
  [REPORT_TYPES.CONVERSION]: quaters
};

export function CreateProject({
  refetch,
  onClose,
  onOpenAddAifs,
  isOpenAddAifs,
  selectedReportType,
  setSelectedReportType,
  isUpdate,
  defaultValues,
  workflowId,
  isAutomation = false,
  submitLabel = 'Continue'
}: CreateProjectProps) {
  const { control, handleSubmit, watch, reset, setValue } = useForm<any>({
    defaultValues: {
      projectName: defaultValues?.projectName,
      endDate: defaultValues?.endDate ? dayjs(defaultValues.endDate).format('YYYY-MM-DD') : null
    }
  });

  const navigate = useNavigate();

  const { user, permission } = authStore as unknown as {
    user?: { name?: string };
    permission?: unknown;
  };
  const isFormPFEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.FORM_PF);

  const oxFlowLocation = isOxFlowLocation();
  const hasPermissionToCustomWorkflow = permission?.[PERMISSION_KEYS.CUSTOM_WORKFLOW];
  const hasPermissionToStandardWorkflow = permission?.[PERMISSION_KEYS.STANDARD_WORKFLOW];
  const endDate = watch('endDate');
  const projectName = watch('projectName');

  const [quater, setQuater] = useState<string>(
    (defaultValues?.period && quaterLabelsByKey[defaultValues.period]) ??
      defaultValues?.period ??
      'Q1'
  );
  const [year, setYear] = useState<string | number>(defaultValues?.year ?? '2025');
  const [fillingType, setFillingType] = useState<string>(
    defaultValues?.filing_type ?? FILLING_TYPE.UPDATING
  );
  const [selectedFillingType, setSelectedFillingType] = useState<string>(
    defaultValues?.filing_time ?? FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
  );
  const [selectedPeriodicFillingVersion, setSelectedPeriodicFillingVersion] = useState<string>(
    defaultValues?.version ?? PERIODIC_FILLING_VERSION_KEYS.LEGACY
  );

  const isFormPF = selectedReportType === REPORT_TYPES.FORM_PF;

  /* Every field the form is seeded with, re-seeded when the subject changes.
   *
   * The quarter and the year were already here; the four below were not, so a second period opened
   * without remounting kept the first one's filing type, version, end date and name. That is what
   * the callers' `key` has been hiding — and a remount is also why the redesigned page's drawer
   * could not animate: it had no closed state to open from. With this, the caller can keep it
   * mounted and simply hand it another period. The classic page still keys it and is unaffected:
   * each of these is a set to the value the state already holds. */
  useEffect(() => {
    if (defaultValues?.period) {
      setQuater(isFormPF ? quaterLabelsByKey[defaultValues.period] : defaultValues.period);
    }
    if (defaultValues?.year) {
      setYear(defaultValues.year);
    }
    if (defaultValues?.filing_type) setFillingType(defaultValues.filing_type);
    if (defaultValues?.filing_time) setSelectedFillingType(defaultValues.filing_time);
    if (defaultValues?.version) setSelectedPeriodicFillingVersion(defaultValues.version);
    if (defaultValues?.endDate) {
      setValue('endDate', dayjs(defaultValues.endDate).format('YYYY-MM-DD'));
    }
    if (defaultValues?.projectName != null) setValue('projectName', defaultValues.projectName);
  }, [
    defaultValues?.period,
    defaultValues?.year,
    defaultValues?.filing_type,
    defaultValues?.filing_time,
    defaultValues?.version,
    defaultValues?.endDate,
    defaultValues?.projectName,
    isFormPF,
    setValue
  ]);
  const [selectedCurrentReportsSection, setSelectedCurrentReportsSection] = useState<string>(
    CURRENT_REPORTS_SECTION_KEYS.SECTION_5
  );
  const [createdWorkflowId, setCreatedWorkflowId] = useState<string | null>(null);
  const [expression, setExpression] = useState<string>('');

  const createProject = useCreateWorkflow();
  const updateProject = useUpdateWorkflow();
  const createPeriod = useCreatePeriod();
  const createPeriodAutomatic = useCreatePeriodAutomatic();
  const updatePeriod = useUpdatePeriod();

  const onChangeQuater = (value: string) => {
    setQuater(value);
  };

  const onChangeYear = (value: string | number) => {
    setYear(value);
  };

  const createFundBulk = useCreateFundsBulk();

  useEffect(() => {
    if (oxFlowLocation && selectedReportType !== REPORT_TYPES.CUSTOM && setSelectedReportType) {
      setSelectedReportType(REPORT_TYPES.CUSTOM);
    }
  }, [oxFlowLocation, selectedReportType, setSelectedReportType]);

  const onSubmit = () => {
    if (
      selectedReportType === REPORT_TYPES.CONVERSION ||
      selectedReportType === REPORT_TYPES.CUSTOM
    ) {
      handleCreateFunds([]);
      return;
    }

    if (isUpdate) {
      update();
      return;
    }

    onOpenAddAifs!();
  };

  const handleChangeReportType = (type: string) => {
    if (setSelectedReportType) setSelectedReportType(type);
    setQuater(
      type === REPORT_TYPES.FORM_PF
        ? quaterLabelsByKey[quater]
        : isFormPF
          ? quaterKeysByLabel[quater]
          : quater
    );
  };

  const handleCreatePeriod = (payload: any, workflowRespsonse?: any, names?: string[]) => {
    (isAutomation ? createPeriodAutomatic : createPeriod).mutate(payload, {
      onSuccess: (fundResult: any) => {
        if (workflowId) {
          onClose?.();
          refetch?.();
          return;
        }

        if (selectedReportType === REPORT_TYPES.CONVERSION) {
          toast.success('Project created successfully');
          return navigate(`/project/quaters/${workflowRespsonse.id}`);
        }

        if (selectedReportType === REPORT_TYPES.CUSTOM) {
          // Custom has no AIFs/Jurisdictions stages — Source Files is the
          // entry, which lives on UploadFiles for both flows now.
          toast.success('Project created successfully');
          return navigate(
            `/project/quaters/${workflowRespsonse.id}/${fundResult?.id}/upload-files`
          );
        }

        createFundBulk.mutate(
          { periodId: fundResult.id, data: { names }, workflowId: workflowRespsonse.id },
          {
            onSuccess: () => {
              if (selectedReportType === REPORT_TYPES.CONVERSION) {
                return navigate(`/project/quaters/${workflowRespsonse.id}`);
              }

              // AIFs is the entry step for scratch Annex IV / Form PF
              // workflows — land on AddAIFs instead of the legacy /funds URL.
              navigate(`/project/quaters/${workflowRespsonse.id}/${fundResult?.id}/add-aifs`);
              onClose?.();
            },
            onError: () => {
              toast.error('Error on creating funds');
            }
          }
        );
      },
      onError: (err: any) => {
        toast.error(`Failed to create period.  ${getErrorMessage(err)}`);
      }
    });
  };

  const handleUpdatePeriod = (payload: any) => {
    updatePeriod.mutate(
      { data: payload, periodId: defaultValues?.id },
      {
        onSuccess: () => {
          refetch?.();
          onClose?.();
        },
        onError: (err: any) => {
          toast.error(`Failed to update period. ${getErrorMessage(err)}`);
        }
      }
    );
  };

  const create = (names?: string[]) => {
    if (!createdWorkflowId)
      return createProject.mutate(
        {
          name: watch('projectName'),
          report_type: selectedReportType
        },
        {
          onSuccess: (workFlowResult: any) => {
            const isFormPF = selectedReportType === REPORT_TYPES.FORM_PF;

            (authStore as any).setWorkflow(workFlowResult.id, workFlowResult.name);
            refetch?.();

            const yearFromEndDate = dayjs(endDate).get('year');
            const payload: any = {
              year: isFormPF ? yearFromEndDate : year,
              period: isFormPF ? quaterKeysByLabel[quater] : quater,
              workflow_id: workFlowResult.id,
              is_from_template: false
            };

            if (isFormPF) {
              payload.filing_time =
                selectedFillingType === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
                  ? selectedFillingType
                  : selectedCurrentReportsSection;
              payload.version = selectedPeriodicFillingVersion;
              payload.end_date = endDate;
              payload.filing_type = fillingType;
            }
            setCreatedWorkflowId(workFlowResult.id);
            handleCreatePeriod(payload, workFlowResult, names);
            setExpression('');
          },
          onError: (err: any) => {
            toast.error(getErrorMessage(err));
            reportError('CreateProject.create', err);
          }
        }
      );

    updateProject.mutate(
      {
        id: createdWorkflowId || workflowId,
        data: {
          name: watch('projectName'),
          report_type: selectedReportType
        }
      },
      {
        onSuccess: () => {
          const isFormPF = selectedReportType === REPORT_TYPES.FORM_PF;

          const yearFromEndDate = dayjs(endDate).get('year');
          const payload: any = {
            year: isFormPF ? yearFromEndDate : year,
            period: isFormPF ? quaterKeysByLabel[quater] : quater,
            workflow_id: workflowId,
            is_from_template: false
          };

          if (isFormPF) {
            payload.filing_time =
              selectedFillingType === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
                ? selectedFillingType
                : selectedCurrentReportsSection;
            payload.version = selectedPeriodicFillingVersion;
            payload.end_date = endDate;
            payload.filing_type = fillingType;
            payload.workflow_id = createdWorkflowId || workflowId;
          }

          handleCreatePeriod(payload, { id: createdWorkflowId }, names);
        },
        onError: (err: any) => {
          toast.error(getErrorMessage(err));
          reportError('CreateProject.update', err);
        }
      }
    );
  };

  const update = (_names?: string[]) => {
    const isFormPF = selectedReportType === REPORT_TYPES.FORM_PF;

    const yearFromEndDate = dayjs(endDate).get('year');
    const payload: any = {
      year: isFormPF ? yearFromEndDate : year,
      period: isFormPF ? quaterKeysByLabel[quater] : quater,
      workflow_id: workflowId
    };

    if (isFormPF) {
      payload.filing_time =
        selectedFillingType === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
          ? selectedFillingType
          : selectedCurrentReportsSection;
      payload.version = selectedPeriodicFillingVersion;
      payload.end_date = endDate;
      payload.filing_type = fillingType;
    }

    if (!defaultValues?.id) return handleCreatePeriod(payload);

    handleUpdatePeriod(payload);
  };

  const handleCreateFunds = (names?: string[]) => {
    if (isUpdate) return update();

    create(names);
  };

  const mutatedReportTypeList = useMemo(() => {
    let res: any[] = [];

    if (hasPermissionToStandardWorkflow && !oxFlowLocation) {
      res = [...res, ...reportTypesList.filter(el => el.key !== REPORT_TYPES.CUSTOM)];
    }

    if (hasPermissionToCustomWorkflow && oxFlowLocation) {
      const customReport = reportTypesList.find(el => el.key === REPORT_TYPES.CUSTOM);
      if (customReport) res.push(customReport);
    }

    if (!isFormPFEnabled) {
      res = res.filter(el => el.key !== REPORT_TYPES.FORM_PF);
    }

    return res;
  }, [
    reportTypesList,
    hasPermissionToCustomWorkflow,
    hasPermissionToStandardWorkflow,
    oxFlowLocation,
    isFormPFEnabled
  ]);

  const showCalculator =
    selectedReportType === REPORT_TYPES.FORM_PF &&
    selectedFillingType === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING &&
    selectedPeriodicFillingVersion === PERIODIC_FILLING_VERSION_KEYS.LEGACY;

  const renderReportTypes = () => {
    return (
      <Flex direction="column">
        <Text size="14px" c="dark.6" fw={600} mb={8}>
          Select Report Type
        </Text>
        <Flex justify="space-between" gap={16} mb={16}>
          {mutatedReportTypeList.map((rep, idx) => {
            const isActive = rep.key === selectedReportType;

            return (
              <Box
                key={idx}
                w="100%"
                p="12px"
                display="flex"
                bg={isActive ? '#E7F5FF' : 'transparent'}
                onClick={isUpdate ? () => {} : () => handleChangeReportType(rep.key)}
                opacity={isUpdate ? 0.5 : 1}
                sx={() => ({
                  alignItems: 'center',
                  flexDirection: 'column',
                  borderRadius: '8px',
                  border: `1px solid ${isActive ? '#74C0FC' : '#F1F3F5'}`,
                  cursor: !isUpdate ? 'pointer' : undefined
                })}
              >
                <Radio checked={isActive} />
                <Text mt="8px" fz="16px" c="black.9">
                  {rep.title}
                </Text>
                <Text mt="4px" fz="13px" ta="center" c="gray.6">
                  {rep.description}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const renderFillingTypes = () => {
    if (selectedReportType !== REPORT_TYPES.FORM_PF) return null;

    return (
      <Flex direction="column">
        <Text size="14px" c="dark.6" fw={600} mb={8}>
          Select Filing Type
        </Text>
        <Flex justify="space-between" gap={16} mb={16}>
          {FORM_PF_FILLING_TYPE_OPTIONS.map((rep, idx) => {
            const isActive = rep.value === selectedFillingType;

            return (
              <Box
                key={idx}
                w="100%"
                p="12px"
                display="flex"
                bg={isActive ? '#E7F5FF' : 'transparent'}
                onClick={() => setSelectedFillingType(rep.value)}
                sx={() => ({
                  alignItems: 'center',
                  flexDirection: 'column',
                  borderRadius: '8px',
                  border: `1px solid ${isActive ? '#74C0FC' : '#F1F3F5'}`,
                  cursor: 'pointer'
                })}
              >
                <Radio checked={isActive} />
                <Text mt="8px" fz="16px" c="black.9">
                  {rep.label}
                </Text>
                <Text mt="4px" fz="13px" ta="center" c="gray.6">
                  {rep.description}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const renderPeriodicFillingVersions = () => {
    if (selectedReportType !== REPORT_TYPES.FORM_PF) return null;
    if (selectedFillingType !== FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING) return null;

    return (
      <Flex direction="column">
        <Text size="14px" c="dark.6" fw={600} mb={8}>
          Select Version
        </Text>
        <Flex justify="space-between" gap={16} mb={16}>
          {PERIODIC_FILLING_VERSION_OPTIONS.map((rep, idx) => {
            const isActive = rep.value === selectedPeriodicFillingVersion;

            return (
              <Box
                key={idx}
                w="100%"
                p="12px"
                display="flex"
                bg={isActive ? '#E7F5FF' : 'transparent'}
                onClick={() => setSelectedPeriodicFillingVersion(rep.value)}
                sx={() => ({
                  alignItems: 'center',
                  flexDirection: 'column',
                  borderRadius: '8px',
                  border: `1px solid ${isActive ? '#74C0FC' : '#F1F3F5'}`,
                  cursor: 'pointer'
                })}
              >
                <Radio checked={isActive} />
                <Text mt="8px" fz="16px" c="black.9">
                  {rep.label}
                </Text>
                <Text mt="4px" fz="13px" ta="center" c="gray.6">
                  {rep.description}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const renderCurrentReportsSections = () => {
    if (selectedReportType !== REPORT_TYPES.FORM_PF) return null;
    if (selectedFillingType !== FORM_PF_FILLING_TYPE_KEYS.CURRENT_REPORTS) return null;

    return (
      <Flex direction="column">
        <Text size="14px" c="dark.6" fw={600} mb={8}>
          Select Section
        </Text>
        <Flex justify="space-between" gap={16} mb={16}>
          {CURRENT_REPORTS_SECTION_OPTIONS.map((rep, idx) => {
            const isActive = rep.value === selectedCurrentReportsSection;

            return (
              <Box
                key={idx}
                w="100%"
                p="12px"
                display="flex"
                bg={isActive ? '#E7F5FF' : 'transparent'}
                onClick={() => setSelectedCurrentReportsSection(rep.value)}
                sx={() => ({
                  alignItems: 'center',
                  flexDirection: 'column',
                  borderRadius: '8px',
                  border: `1px solid ${isActive ? '#74C0FC' : '#F1F3F5'}`,
                  cursor: 'pointer'
                })}
              >
                <Radio checked={isActive} />
                <Text mt="8px" fz="16px" c="black.9">
                  {rep.label}
                </Text>
                <Text mt="4px" fz="13px" ta="center" c="gray.6">
                  {rep.description}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const renderFormBody = () => {
    return (
      <>
        <Flex display="flex" gap={16}>
          <Select
            disabled
            label="Assign to"
            mb="16px"
            w="100%"
            value={user?.name}
            data={[user?.name] as string[]}
            rightSection={<IoIosArrowDown />}
          />

          <Box w="100%">
            <FormInput
              w="100%"
              disabled={isUpdate}
              name="projectName"
              data-testid="project-name"
              control={control}
              label={`${REPORT_NAMINGS.PROJECT[selectedReportType]} Name`}
              required
            />
          </Box>
        </Flex>

        <Flex gap={16}>
          {
            <Input.Wrapper label="Select Period" mt="16" required w="100%">
              <Flex align="center" mt="2">
                <Grid w="100%" gutter="sm" grow>
                  {quatersByReportType[selectedReportType].map(value => (
                    <Grid.Col key={value} span={2} onClick={() => onChangeQuater(value)}>
                      <Flex
                        style={{
                          borderRadius: '4px',
                          color: quater === value ? '#fff' : '#000',
                          cursor: 'pointer'
                        }}
                        justify="center"
                        bg={quater === value ? 'blue.6' : 'gray.1'}
                        py="4"
                        fz="sm"
                      >
                        {value}
                      </Flex>
                    </Grid.Col>
                  ))}
                </Grid>
              </Flex>
            </Input.Wrapper>
          }

          {!isFormPF && (
            <Input.Wrapper label="Select Year" mt="16" w="100%" required>
              <Flex align="center" mt="2">
                <Grid w="100%" gutter="sm" grow>
                  {years.map(value => (
                    <Grid.Col key={value} span={3} onClick={() => onChangeYear(value)}>
                      <Flex
                        style={{
                          borderRadius: '4px',
                          color: year === value ? '#fff' : '#000',
                          cursor: 'pointer'
                        }}
                        justify="center"
                        bg={year === value ? 'blue.6' : 'gray.1'}
                        py="4"
                        fz="sm"
                      >
                        {value}
                      </Flex>
                    </Grid.Col>
                  ))}
                </Grid>
              </Flex>
            </Input.Wrapper>
          )}
        </Flex>

        {isFormPF && (
          <Box mt={8}>
            <FormInput
              w="100%"
              required
              type="date"
              name="endDate"
              data-testid="endDate"
              control={control}
              label="Select the end date of this reporting period"
              description="You can set fund-specific end dates later on the report page"
            />
          </Box>
        )}

        {isFormPF && selectedFillingType === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING && (
          <Select
            label="Filing Type"
            data={FILLING_TYPE_ARRAYS}
            value={fillingType}
            onChange={val => val && setFillingType(val)}
            mt={8}
            renderOption={item => {
              return (
                <Flex align="center" justify="space-between" w="100%">
                  <Text fw={400}>{item.option.label}</Text>

                  <Tooltip label={(item.option as { description?: string }).description} withArrow>
                    <ActionIcon variant="subtle">
                      <IoInformationCircleOutline />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              );
            }}
            rightSection={<IoIosArrowDown />}
            w="50%"
          />
        )}
      </>
    );
  };

  const disableNextButtonInWorkflowStep = useMemo(() => {
    if (selectedReportType === REPORT_TYPES.FORM_PF) {
      return !endDate || !projectName || projectName.trim() === '' || !quater;
    }

    return !projectName || projectName.trim() === '' || !quater;
  }, [selectedReportType, year, quater, endDate, projectName]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isOpenAddAifs ? (
        <AddAIFs
          selectedReportType={selectedReportType}
          isLoading={createProject.isLoading || createPeriod.isLoading || createFundBulk.isLoading}
          handleCreate={handleCreateFunds}
          expression={expression}
          setExpression={setExpression}
        />
      ) : (
        <>
          {renderReportTypes()}

          {renderFillingTypes()}

          {renderPeriodicFillingVersions()}

          {renderCurrentReportsSections()}

          {showCalculator && (
            <Box my={8}>
              <FormPFDeadlineCalculator />
            </Box>
          )}

          {renderFormBody()}

          <Flex justify="flex-end" mt="48px" pos="sticky" bottom={10} bg="white">
            <Button
              loading={
                createProject.isLoading ||
                createPeriod.isLoading ||
                updatePeriod.isLoading ||
                updateProject.isLoading ||
                createPeriodAutomatic.isLoading
              }
              type="submit"
              data-testid="submit-btn"
              rightSection={!isUpdate && <IoIosArrowForward />}
              size="sm"
              disabled={disableNextButtonInWorkflowStep}
            >
              {submitLabel}
            </Button>
          </Flex>
        </>
      )}
    </form>
  );
}
